import isEmpty from 'lodash/isEmpty';
import isNil from 'lodash/isNil';

import axios from './axios.real';

import { receiveRoles } from 'modules/access/reducers/receive-roles';
import { decodeToken, getAccessToken, getRefreshToken, setTokens } from './token';
import { camelCaseKeys, snakeCaseKeys, trimPayload } from './keys';
import {
  API_CODE_CREDENTIALS_MISSING,
  API_CODE_TOKEN_INVALID,
  API_CODE_TOKEN_REVOKED,
  NOT_FOUND_STATUS_CODE,
  UNAUTHORIZED_STATUS_CODE
} from './status';
import { __t, applyTranslations } from './language';
import { logError } from './log';
import {
  dispatchWithShameNotificationAction
} from './store-ref';
import { valueOrEmptyString } from './url';

import type {
  ApiConfig,
  ApiMethod,
  ApiOptions,
  CampaignType,
  ChartType,
  RealmOutfitStatus,
  RecentsResourceType,
  Role,
  SOVReportType
} from 'types';
import type {
  ReportType as InsightsReportsReportType
} from 'modules/insights/reports/types';

type NumberNullUndefined = number | null | undefined;

applyTranslations(require('./i18n/api-util.i18n.json'));

// this can not be exposed outside but some endpoints are harcoded and we need
// to use it... after we refactor how api.js is working we need to remove
// the export condition
export const ROOT_PATH = {
  IDENTITY: `${window.app.env.COLLAB_GATEWAY_URL}/api/iam`,
  COLLAB_BE: `${window.app.env.API_URL}`,
  API: `${window.app.env.COLLAB_GATEWAY_URL}/api/collab`,
  CONFIG: `${window.app.env.RUNTIME_CONFIG_URL}`,
  CCM_ASSETS: {
    CCM_LEGAL_REQUIREMENTS_TEMPLATE_URL: `${window.app.env.CCM_LEGAL_REQUIREMENTS_TEMPLATE_URL}`
  },
  PDFKITTY: `${window.app.env.COLLAB_GATEWAY_URL}/api/pdfkitty`,
  RAVEN: `${window.app.env.COLLAB_GATEWAY_URL}/api/notification`
};

function getCreatorIdsQuery(influencerIds: Array<number>): string {
  return influencerIds.length > 0 ? `creator_ids=${influencerIds.join(',')}` : '';
}

const getInsightsEndpoints = (endpointType: 'sov' | 'insights') => ({
  getAudienceData: (reportId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/audience-data`
  ),
  getReports: (brandProfileId: number) => (
    `${ROOT_PATH.API}/${endpointType}/${brandProfileId}/reports`
  ),
  getReportById: (brandProfileId: number, reportId: number) => (
    `${ROOT_PATH.API}/${endpointType}/${brandProfileId}/reports/${reportId}`
  ),
  fetchQueries: (reportId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries`
  ),
  fetchQueriesMulti: (reportId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/multi`
  ),
  getQueryByQueryId: (reportId: NumberNullUndefined, queryId: number) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${queryId}`
  ),
  getKeywords: (name: string, queryId?: number | null) => (
    `${ROOT_PATH.API}/${endpointType}/queries/${name}/search?query_id=${valueOrEmptyString(queryId)}`
  ),
  getChannels: () => (
    `${ROOT_PATH.API}/${endpointType}/channels`
  ),
  getCountries: () => (
    `${ROOT_PATH.API}/${endpointType}/countries`
  ),
  getPosts: (reportId: NumberNullUndefined, page: number, queryIds: Array<number>, profileTypes: Array<string>, tiers: string, channel: string, sort: string, reportType: SOVReportType | InsightsReportsReportType) =>
    `${ROOT_PATH.API}/${endpointType}/${reportType}/reports/${valueOrEmptyString(reportId)}/posts?page=${page}&query_id=${queryIds.join(',')}${profileTypes.join('')}${tiers}&channel_name=${channel}&sort=${sort}`,
  createReport: (brandProfileId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/${brandProfileId}/reports`
  ),
  updateReport: (reportId: NumberNullUndefined, brandProfileId: NumberNullUndefined, query?: string) => (
    `${ROOT_PATH.API}/${endpointType}/${brandProfileId}/reports/${reportId}${query || ''}`
  ),
  createQuery: (reportId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries`
  ),
  createQueryMulti: (reportId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/multi`
  ),
  updateQuery: (reportId: NumberNullUndefined, queryId?: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${String(queryId)}`
  ),
  updateQueryMulti: (reportId: NumberNullUndefined, queryId?: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${String(queryId)}/multi`
  ),
  deleteReport: (brandProfileId: number, reportId: number) => (
    `${ROOT_PATH.API}/${endpointType}/${brandProfileId}/reports/${reportId}`
  ),
  deleteQuery: (reportId: NumberNullUndefined, queryId: number) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${queryId}`
  ),
  deleteQueryMulti: (reportId: NumberNullUndefined, queryId: number) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${queryId}/multi`
  ),
  cloneReport: (reportId: number | undefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/clone`
  ),
  cloneQuery: (reportId: NumberNullUndefined, queryId?: number | null) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${String(queryId)}/clone`
  ),
  cloneQueryMulti: (reportId: NumberNullUndefined, queryId?: number | null) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/queries/${String(queryId)}/clone/multi`
  ),
  runAnalysis: (reportId: NumberNullUndefined, reportType: SOVReportType | InsightsReportsReportType) => (
    `${ROOT_PATH.API}/${endpointType}/${reportType}/reports/${reportId}/analysis`
  ),
  getTopInfluencers: (reportId: NumberNullUndefined, reportType: SOVReportType | InsightsReportsReportType) => (
    `${ROOT_PATH.API}/${endpointType}/${reportType}/reports/${reportId}/analysis/top-creators`
  ),
  downloadAnalysis: (reportId: NumberNullUndefined) => (
    `${ROOT_PATH.API}/${endpointType}/reports/${reportId}/analysis/export`
  ),
  addPost: () => (
    `${ROOT_PATH.API}/${endpointType}/posts`
  )
});

export const API = {
  END_POINTS: {
    SEARCH: {
      CONTENT_CREATORS: `${ROOT_PATH.API}/discovery/creators`,
      ORGANIZATIONS: `${ROOT_PATH.API}/organizations`,
      INFLUENCER_PROFILES: `${ROOT_PATH.API}/admin/influencer-profiles/search`
    },
    DISCOVERY: {
      creators: {
        getCreators: (queryString: string) => `${ROOT_PATH.API}/discovery/creators${queryString ? `?${queryString}` : ''}`,
        taxonomies: () => `${ROOT_PATH.API}/taxonomies`
      },
      content: {
        getResults: (brandProfileId: number, page: number, sort: string, itemsPerPage: number) =>
          `${ROOT_PATH.API}/brands/${brandProfileId}/content-discovery?page=${page}&sort=${sort}&items_per_page=${itemsPerPage}`
      }
    },
    CONTENT_LISTS: {
      getDashboardLists: (orgId: number, type: string, filter: string, page: number, sortBy: string, itemsPerPage: number, search: string = '') => (
        `${ROOT_PATH.API}/organizations/${orgId}/content-lists?type=${type}&filter=${filter}&page=${page}&sort_by=${sortBy}&items_per_page=${itemsPerPage}&search=${search}`
      ),
      getList: (orgId: number, listId?: number) => `${ROOT_PATH.API}/organizations/${orgId}/content-lists/${valueOrEmptyString(listId)}`,
      getSmartList: (orgId: number, listId: string) => `${ROOT_PATH.API}/organizations/${orgId}/content-lists/smart/${listId}`,
      getSmartListAttachments: (orgId: number, listId: string, filename: string) => `${ROOT_PATH.API}/organizations/${orgId}/content-lists/smart/${listId}/download?filename=${encodeURIComponent(filename)}`,
      getPost: (orgId: number, assetId: string) => `${ROOT_PATH.API}/organizations/${orgId}/content-lists/smart/all-campaign-posts/${assetId}`
    },
    LISTS: {
      getDashboardLists: (orgId: number, type: string, filter: string, page: number, sortBy: string, itemsPerPage: number, search: string = '') => (
        `${ROOT_PATH.API}/organizations/${orgId}/lists?type=${type}&filter=${filter}&page=${page}&sort_by=${sortBy}&items_per_page=${itemsPerPage}&search=${search}`
      ),
      getList: (orgId: number, listId?: number, channel?: string | null) => (
        `${ROOT_PATH.API}/organizations/${orgId}/lists/${valueOrEmptyString(listId)}${channel ? `?channel=${channel}` : ''}`
      ),
      getListAggregates: (orgId: number, listId: number, channel: string) => `${ROOT_PATH.API}/organizations/${orgId}/lists/${listId}/aggregations?channel=${channel}`,
      duplicateList: (orgId: number, listId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lists/${listId}/duplicate`,
      getLists: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lists`,
      getListsV2: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lists/v2`,
      getListInfluencers: (orgId: number, listId: number | string | undefined) => `${ROOT_PATH.API}/organizations/${orgId}/lists/${listId}/creators`,
      getInfluencersHistory: (orgId: number, influencerIds: Array<number>) =>
        `${ROOT_PATH.API}/organization/${orgId}/campaign-history-availability?${getCreatorIdsQuery(influencerIds)}`,
      getAudienceOverlap: (orgId: number, listId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lists/${listId}/audience-overlap`,
      getCustomizableAudienceOverlap: (orgId: number, listId: number, influencerId: number) =>
        `${ROOT_PATH.API}/organizations/${orgId}/lists/${listId}/creator-audience-overlap?influencer_id=${influencerId}`,
      getBulkCreators: (orgId: number, collectionId: number, sort?: string | null, totalItems?: number | null) => (
        `${ROOT_PATH.API}/organizations/${orgId}/lists/${collectionId}/creators-all?page=1${sort ? `&sort=${sort}` : ''}${totalItems ? `&items_per_page=${totalItems}` : ''}`
      ),
      uploadXLSToParseLightweightInfluencers: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lightweight-influencer-bulk/import-tasks/xls`,
      saveBulkLightweightInfluencers: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lightweight-influencer-bulk/import-tasks`,
      getImportInfluencersTaskInfo: (orgId: number, taskId: number) => `${ROOT_PATH.API}/organizations/${orgId}/lightweight-influencer-bulk/import-tasks/${taskId}`
    },
    RECENTS: {
      getRecents: (resourceType: RecentsResourceType, orgId: number, creatorId: number, limit: number = 4) => (
        `${ROOT_PATH.API}/organizations/${orgId}/recents/${resourceType}?creator_id=${creatorId}&limit=${limit}`
      )
    },
    REPORTING: {
      getReportSummary: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/summary`
      ),
      fetchKpiSettings: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/options`
      ),
      saveKpiSettings: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/options`
      ),
      getTikTokReport: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/tiktok`
      ),
      getIgPostReport: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/instagram-post`
      ),
      getAudienceData: (campaignId: number, creatorId: number, filter: string) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/influencer-profiles/${creatorId}/reach-audience/${filter}`
      ),
      getIgStoryReport: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/instagram-story`
      ),
      getIgReelReport: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/instagram-reel`
      ),
      getIgPostAudienceData: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/reach-audience/instagram-post`
      ),
      getIgStoryAudienceData: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/reach-audience/instagram-story`
      ),
      getIgReelAudienceData: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/reach-audience/instagram-reel`
      ),
      creatorPerformanceReport: (campaignId: number, creatorId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/creator-performance/${String(creatorId)}`
      ),
      onsitePerformance: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/report/v2/zalando-onsite`
      ),
      requestNotableLikesData: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/v2/likers-report-requests`
      ),
      getTiktokAudienceData: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${String(campaignId)}/reach-audience/tiktok-video`
      )
    },
    REPORTS: {
      customReportV2: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/report/v2/custom`,
      customReportV2AsyncGenerate: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/report/v2/async/custom`,
      customReportV2AsyncCheckStatus: (taskId: number) => (
        `${ROOT_PATH.API}/report/async/task/${taskId}/status`
      ),
      customReportV2AsyncDownload: (taskId: number) => (
        `${ROOT_PATH.API}/report/async/task/${taskId}/content`
      ),
      metrics: {
        getDeliverables: (campaignId: number, query: string = '') => (
          `${ROOT_PATH.API}/v2/campaigns/${campaignId}/deliverables${query ? `?${query}` : ''}`
        ),
        createAdditionalDeliverable: (offerId: number) => (
          `${ROOT_PATH.API}/admin/offers/${offerId}/deliverables/additional`
        ),
        updateAdditionalDeliverable: (offerId: number, deliverableId: number) => (
          `${ROOT_PATH.API}/admin/offers/${offerId}/deliverables/additional/${deliverableId}`
        ),
        deleteAdditionalDeliverable: (offerId: number, deliverableId: number) => (
          `${ROOT_PATH.API}/admin/offers/${offerId}/deliverables/additional/${deliverableId}`
        ),
        getDeliverablesQueryOnly: (query: string = '') => (
          `${ROOT_PATH.API}/v2/admin/deliverables${query ? `?${query}` : ''}`
        ),
        addMetrics: (campaignId: number) => (
          `${ROOT_PATH.API}/campaign/${campaignId}/metrics/`
        ),
        updateMetrics: (campaignId: number, metricId: number) => (
          `${ROOT_PATH.API}/campaign/${campaignId}/metrics/${metricId}`
        ),
        deleteStoryMetrics: (campaignId: number, metricId: number) => (
          `${ROOT_PATH.API}/campaign/${campaignId}/metrics/story-metrics/${metricId}`
        ),
        unmatchAnalyticAsset: (deliverableId: number, metricId: number, assetId: number) => (
          `${ROOT_PATH.API}/deliverables/${deliverableId}/metrics/${metricId}/matched/${assetId}`
        ),
        matchAssetToAnalytic: (deliverableId: number, metricId: number, type: 'analytics' | 'snippet') => (
          `${ROOT_PATH.API}/deliverables/${deliverableId}/metrics/${metricId}/matched/${type}`
        ),
        fetchReportMetricAnalytic: (deliverableId: number) => (
          `${ROOT_PATH.API}/deliverables/${deliverableId}/metrics/`
        ),
        reorderReportMetrics: (deliverableId: number, metricId: number) => (
          `${ROOT_PATH.API}/deliverables/${deliverableId}/metrics/${metricId}/order`
        )
      },
      saveBudgetSplit: () => (
        `${ROOT_PATH.API}/budget-splits`
      ),
      budgetSplitsV2: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/budget-splits`
      ),
      bulkBudgetSplits: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/calculate-bulk-budget-splits`
      ),
      offerGroups: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/offer-groups-by-task-types`
      ),
      reportMetaInfo: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/report-meta`
      )
    },
    AGENCY: {
      getBrands: (agencyId: number) => `${ROOT_PATH.API}/agencies/${agencyId}/brand-profiles`,
      createBrandProfile: (agencyId: number) => `${ROOT_PATH.API}/agencies/${agencyId}/brand-profiles`
    },
    BRANDS: {
      getBrandProfile: (brandProfileId: number) => `${ROOT_PATH.API}/brand-profiles/${brandProfileId}`,
      getBrandProfilesInBulk: (brandProfileIds: Array<number>) => `${ROOT_PATH.API}/brand-profiles?ids=${brandProfileIds.join(',')}`,
      createBrandProfile: (organizationId: number) => `${ROOT_PATH.API}/organizations/${organizationId}/brand-profiles`,
      updateBrandProfile: (brandProfileId: number) => `${ROOT_PATH.API}/brand-profiles/${brandProfileId}`,
      getBrandCampaignsV1: (organizationId: number) => `${ROOT_PATH.API}/v1/organizations/${organizationId}/campaigns`
    },
    INFLUENCERS: {
      getInfluencerProfile: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}`,
      getInfluencerChannels: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/channels?include=metrics`,
      getInfluencerLatestChannelPosts: (influencerProfileId: number, channelId: number) =>
        `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/channels/${channelId}/posts?order=desc`,
      getInfluencerSubcampaigns: (influencerProfileId: number, query?: string | null) =>
        `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/paginated-subcampaigns?${query || ''}&campaign_type=collab`,
      getInfluencerCampaignHistory: (organizationId: number, creatorId: number) =>
        `${ROOT_PATH.API}/organization/${organizationId}/content-creator/${creatorId}/campaign-history`,
      getInfluencerRelatedInfluencers: (creatorId: number, conditions: string) => `${ROOT_PATH.API}/creator-profiles/${creatorId}/similar?${conditions}`,
      getNotableFollowers: (influencerProfileId: number, queryString: string) => (
        `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/relation-graph${queryString}`
      ),
      getNotableFollowersList: (influencerProfileId: number, includeBrandChannels: boolean) => (
        `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/notable-followers?includeBrandChannels=${String(includeBrandChannels ?? false)}`
      ),
      createInfluencerProfile: (organizationId: number) => `${ROOT_PATH.API}/organizations/${organizationId}/influencer-profiles`,
      createLightweightInfluencerProfile: (organizationId: number | null) => `${ROOT_PATH.API}/organizations/${organizationId}/lightweight-influencer`,
      deleteInfluencerProfile: (influencerProfileId: number, reason: string) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}?reason=${reason}`,
      updateInfluencerProfile: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}`,
      getInfluencersMeta: (orgId: number, influencerIds: Array<number>) => `${ROOT_PATH.API}/organizations/${orgId}/creators-meta?${getCreatorIdsQuery(influencerIds)}`,
      getInstagramInfluencer: (username: string) => `${ROOT_PATH.API}/instagram-influencers/${username}?include=profile_data,profile_image`,
      getInfluencersResponseTime: (ids: Array<number>) => `${ROOT_PATH.API}/reports/response-time?influencersIds=${ids.join(',')}`,
      getInfluencerSelfResponseTime: (id: number) => `${ROOT_PATH.API}/reports/self/response-time?influencerId=${id}`,
      getInfluencersRecentPosts: (query: string) => `${ROOT_PATH.API}/discovery/creators/posts?${query}`,
      getInfluencersAudienceData: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/assets`,
      createInfluencerAudienceData: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/assets`,
      getInfluencersFromSameAgency: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profile/${influencerProfileId}/agency-profiles`,
      getInfluencersMetricsData: () => `${ROOT_PATH.API}/influencers/metrics`,
      facebookTokenByCreatorId: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/facebook-user/token`,
      facebookInfo: () => `${ROOT_PATH.API}/influencer-profiles/facebook-user/token/info`,
      facebookToggleDataEnabled: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/facebook-data-enabled`
    },
    CHANNEL: {
      validateChannelUrl: () => `${ROOT_PATH.API}/channel-validations`
    },
    CAMPAIGNS: {
      getCampaign: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}`,
      getCampaignsBulk: () => `${ROOT_PATH.API}/campaigns`,
      updateCampaign: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}`,
      createCampaign: (brandProfileId: number) => `${ROOT_PATH.API}/brand-profiles/${brandProfileId}/campaigns`,
      duplicateCampaign: (brandProfileId: number, campaignId: number) => `${ROOT_PATH.API}/brand-profiles/${brandProfileId}/campaigns/${campaignId}/copies`,
      getCampaignSubcampaigns: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns`,
      downloadCampaignSubcampaigns: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/creators/export`,
      getCampaignDeliverables: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables`,
      getCampaignGroupedDeliverables: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/grouped-deliverables`,
      getCampaignGroupedDeliverablesIds: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/grouped-deliverable-ids`,
      getCampaignOverview: (campaignId: number, campaignType: CampaignType) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/overview?campaign_type=${campaignType}`
      ),
      campaignOverviewBudgetNote: (campaignId: number, note: string) => `${ROOT_PATH.API}/campaigns/${campaignId}/budget-note?note=${note}`,
      getCampaignsOverviewBulk: () => `${ROOT_PATH.API}/campaigns/overview`,
      getCampaignUISettingsShorlisted: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/ui-settings-shortlisted`,
      saveCampaignUISettingsShorlisted: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/ui-settings-shortlisted`,
      updateCampaignAssets: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/assets`,
      updateCampaignMainImage: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/main-image`,
      removeSubcampaign: (subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}`,
      manageCampaignNotificationSettings: () => `${ROOT_PATH.API}/campaign-notification-settings`,
      createBulkOffers: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/bulk-offers`,
      editBulkOffers: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/bulk-offers`,
      createSubcampaignsInBulk: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/bulk-subcampaigns`,
      downloadShippingAddresses: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/addresses/export`,
      fetchCreatorsFirstTask: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/earliest-deliverables`,
      importCollection: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/lists-subcampaigns`,
      clearShortlist: (campaignId: number) => `${ROOT_PATH.API}/subcampaigns/${campaignId}/draft`,
      downloadDeliverables: (campaignId: number) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables/download`,
      downloadDeliverable: (campaignId: number, deliverableId: number, filename: string) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables/${deliverableId}/attachments/download?filename=${filename}`,
      downloadSnippet: (campaignId: number, deliverableId: number, snippetId: number, filename: string, snippetName: string) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables/${deliverableId}/attachments/${snippetId}/download?approved_only=false&filename=${filename}&snippet_section_name=${snippetName}`,
      getOutfits: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/outfits`,
      getZalandoOutfits: (campaignId: string, statuses: Array<RealmOutfitStatus>) => `${ROOT_PATH.API}/campaigns/${campaignId}/zalando-outfits?outfit_status=${statuses.join(',')}`,
      getCreatorlessOutfits: (campaignId: number) => `${ROOT_PATH.API}/campaigns/creatorless/${campaignId}/outfits`,
      submitCreatorlessOutfit: (campaignId: number) => `${ROOT_PATH.API}/campaigns/creatorless/${campaignId}/outfits`,
      updateCreatorlessOutfit: (campaignId: number, outfitId: number) => `${ROOT_PATH.API}/campaigns/creatorless/${campaignId}/outfits/${outfitId}`,
      createOutfits: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/outfits`,
      deleteProductList: (campaignId: number, id: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/product-lists/${id}`,
      getProductLists: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/product-lists`,
      getProductList: (campaignId: number, productListId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/product-lists/${productListId}`,
      saveProductList: (campaignId: number, productListId?: number | null) => `${ROOT_PATH.API}/campaigns/${campaignId}/product-lists/${productListId || ''}`,
      getContentApproval: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/assets/content-approval`,
      toggleFlexibleSKU: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/product-list-bucket`,
      getIsFlexibleSKU: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/product-list-bucket`,
      getCreatorsList: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/creators`,
      getDeliverableTags: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables/tags`,
      resendExpiredSubcampaignOffers: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/resend-expired`
    },
    CCM: {
      ASSETS: {
        getCcmLegalRequirements: () => ROOT_PATH.CCM_ASSETS.CCM_LEGAL_REQUIREMENTS_TEMPLATE_URL
      },
      createCampaign: (brandProfileId: number) => (
        `${ROOT_PATH.API}/brand-profiles/${brandProfileId}/campaigns`
      ),
      updateCampaign: (campaignId: number | null) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}`
      ),
      approveCampaign: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/approve`
      ),
      getPool: (campaignId: number, queryString: string | null = null) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/pool${!isNil(queryString) ? `?${queryString}` : ''}`
      ),
      generateTimeline: (date: ['start-date' | 'go-live-date', string]) => (
        `${ROOT_PATH.API}/campaigns/timeline?${date[0].replaceAll('-', '_')}=${date[1]}`
      ),
      getCampaignPayments: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/ccm-brand-invoice-info`,
      getCreatorPayoutsByCcmCampaign: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/ccm-payouts`,
      saveMetadata: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/ccm-external-data`
      ),
      getOutfitPerformance: (campaignId: number, countries: string) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/ccm/report/outfit-performance${!isEmpty(countries) ? `?countries=${countries}` : ''}`
      )
    },
    DELIVERABLE: {
      getDeliverableAttachments: (campaignId: number, deliverableId: number) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables/${deliverableId}/attachments`,
      contentApprovalBuckets: (campaignId: number, deliverableId: number, sort?: string) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables/${deliverableId}/content-approval${sort ? `?sort_by=${sort}` : ''}`
      ),
      saveContentApprovalData: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/content-approval/v2`
      ),
      saveInsightsData: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/attachments`
      ),
      markApprovalNoteAsRead: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/content-approval/v2/approval-notes/read`
      ),
      setApprovalStatus: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/content-approval/v2/approval-status`
      ),
      getClientApprovalData: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/content-approval/v2/client-approval`
      ),
      updateClientApprovalData: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/content-approval/v2/client-approval`
      ),
      getLooksForClientApproval: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/content-approval/v2/client-approval/looks`
      ),
      updateLooksForClientApproval: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/content-approval/v2/client-approval/looks`
      ),
      getOfferDeliverable: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverable/${deliverableId}`
      ),
      moveContent: (offerId: number, deliverableId: number) => (
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/attachments/move`
      ),
      editApprovalNote: (noteId: number) => (
        `${ROOT_PATH.API}/approval-notes/${noteId}`
      ),
      modifyDeliverableStoriesList: (deliverableId: number) => (
        `${ROOT_PATH.API}/deliverables/${deliverableId}/facebook/stories`
      ),
      searchDeliverableStories: (deliverableId: number) => (
        `${ROOT_PATH.API}/deliverables/${deliverableId}/facebook/stories/search`
      ),
      getDeliverableStories: (deliverableId: number) => (
        `${ROOT_PATH.API}/deliverables/${deliverableId}/facebook/stories`
      ),
      creatorlessNotes: (campaignId: number) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/approval-notes`
      )
    },
    OFFERS: {
      getOfferDeliverables: (offerId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables`,
      putOfferState: (offerId: number) => `${ROOT_PATH.API}/offers/${offerId}/state`,
      offer: (offerId: number) => `${ROOT_PATH.API}/offers/${offerId}`,
      putDeliverableCreatorNote: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/creator-note`,
      putDeliverableBrandFeedback: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/brand-feedback`,
      offerDeliverableWMCLinks: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/tracking-links`
    },
    ORGANIZATIONS: {
      searchOrganization: (resource: string, resourceId: number) => `${ROOT_PATH.API}/organizations/search?resource=${resource}&resource_id=${resourceId}`,
      getOrganizationSettings: (organizationId: number) => `${ROOT_PATH.API}/organizations/${organizationId}?include=bank_details,billing_address,business_details`,
      downloadOrganizationProofDocument: (organizationId: number, assetId: number) => `${ROOT_PATH.API}/organizations/${organizationId}/proof-documents/${assetId}`,
      updateOrganizationSettings: (organizationId: number) => `${ROOT_PATH.API}/organizations/${organizationId}`
    },
    DELIVERABLE_MEDIA: {
      validateImageAttachment: () => `${ROOT_PATH.API}/lambda-proxy`,
      getOfferDeliverables: (offerId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables`,
      updateOfferPost: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/post`,
      addOfferAttachment: (offerId: number, deliverableId: number, ignoreMetricsCreation?: boolean) =>
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/attachments${ignoreMetricsCreation ? '?ignore_metrics_creation=true' : ''}`,
      removeOfferAttachment: (offerId: number, deliverableId: number, attachmentId: number | string) =>
        `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/attachments/${attachmentId}`,
      createAttachmentsBatch: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/attachments-batch`,
      changeAttachmentsApprovalStatus: (offerId: number, deliverableId: number) => `${ROOT_PATH.API}/offers/${offerId}/deliverables/${deliverableId}/attachments/-/status`
    },
    SUBCAMPAIGNS: {
      getSubcampaignsByCampaignBulk: (campaignIds: Array<number>) => `${ROOT_PATH.API}/v2/subcampaigns?campaign_ids=${campaignIds.join(',')}`,
      getSubcampaign: (subcampaignId: number, includeDetails?: boolean) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}${includeDetails ? '?include=business_details,bank_details,billing_address,payouts' : ''}`,
      cancelSubcampaign: (subcampaignId: number) => `${ROOT_PATH.API}/canceled-subcampaigns/${subcampaignId}`,
      uncancelSubcampaign: (subcampaignId: number) => `${ROOT_PATH.API}/canceled-subcampaigns/${subcampaignId}`,
      createSubcampaignOffer: (subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/offers`,
      putSubcampaignShippingAddress: (subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/shippingaddress`,
      getAggregatedStatistics: (campaignId: number, state: string, channel: string) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/${state}/aggregations?channel=${channel}`,
      getAudienceOverlap: (campaignId: number, state: string) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/${state}/audience-overlap`,
      getCustomizableAudienceOverlap: (campaignId: number, state: string, influencerId: number) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/${state}/creator-audience-overlap?influencer_id=${influencerId}`,
      createCollectionForOfferType: (campaignId: number) =>
        `${ROOT_PATH.API}/campaigns/${campaignId}/export-to-creator-list`,
      removeSubcampaigns: (ids: Array<number>) => `${ROOT_PATH.API}/subcampaigns?subcampaignIds=${ids.join(',')}`,
      sendQuote: (offerId: number) => `${ROOT_PATH.API}/offers/${offerId}/quote`
    },
    PAYOUTS: {
      handlePayoutsByCampaign: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/payouts`,
      handlePayoutsBySubcampaign: (subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/payouts-plan`,
      handleReleaseTotalPayouts: (subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/payouts-plan/release`,
      handleReleasePartialPayout: (payoutId: number, subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/payouts-plan/payouts/${payoutId}/release`,
      handleUnreleasePartialPayout: (payoutId: number, subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/payouts-plan/payouts/${payoutId}/unrelease`,
      handlePurchaseOrder: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/purchase-orders`
    },
    VARIABLE_PAYMENTS: {
      bulkEditVariablePayments: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/bulk-variable-payments`,
      getCampaignVariablePayments: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/variable-payments`,
      getSubcampaignVariablePayments: (campaignId: number, subcampaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/${subcampaignId}/variable-payments`,
      createVariablePayments: (campaignId: number, subcampaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/${subcampaignId}/variable-payments`,
      editVariablePayments: (campaignId: number, subcampaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/subcampaigns/${subcampaignId}/variable-payments`
    },
    ADMIN: {
      getInfluencersWithDuplicatedChannels: (influencerProfileId: number) => `${ROOT_PATH.API}/admin/duplicate-channel-profile-ids/${influencerProfileId}`,
      searchCampaigns: (brandId: number) => `${ROOT_PATH.API}/brand-profiles/${brandId}/campaigns/dropdown?campaign_types=collab`,
      searchDeliverables: (campaignId: number, influencerId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/deliverables?creator_id=${influencerId}`,
      getProfileApprovalUrl: (profileType: string, profileId: number) => `${ROOT_PATH.API}/admin/${profileType}-profiles/${profileId}/approval-status`,
      getChannels: () => `${ROOT_PATH.API}/admin/channels/search`,
      updateChannel: () => `${ROOT_PATH.API}/admin/channels`,
      getUsers: () => `${ROOT_PATH.API}/admin/users`,
      getUserOrganizations: (userId: number) => `${ROOT_PATH.API}/admin/users/${userId}/organizations?include=profiles`,
      getUserRoles: (userId: number) => `${ROOT_PATH.IDENTITY}/users/${userId}/roles`,
      addUserRoles: (userId: number) => `${ROOT_PATH.API}/admin/users/${userId}/roles`,
      deleteUserRole: (userId: number, role: Role) => `${ROOT_PATH.IDENTITY}/users/${userId}/roles/${role}`,
      updateUser: (userId: number) => `${ROOT_PATH.API}/admin/users/${userId}`,
      removeUserFromOrganization: (organizationId: number, userId: number) => `${ROOT_PATH.API}/admin/organizations/${organizationId}/members/${userId}`,
      getCampaigns: () => `${ROOT_PATH.API}/admin/campaigns`,
      getUpdateCampaignEndDateUrl: (campaignId: number) => `${ROOT_PATH.API}/admin/campaigns/${campaignId}/end-date`,
      downloadCampaignWMC: (campaignId: number) => `${ROOT_PATH.API}/admin/campaigns/${campaignId}/tracking-links-report`,
      getSentimentAnalysisDeliverableResults: (deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/comments`,
      downloadSentimentAnalysisAsExcel: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/comments/xlsx`,
      getSentimentAnalysisCampaignResults: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/comments`,
      changeSentimentClass: (deliverableId: number, commentUuid: string) => `${ROOT_PATH.API}/deliverables/${deliverableId}/comments/${commentUuid}`,
      getActiveCampaigns: (influencerId: number, historicalPeriod: number) =>
        `${ROOT_PATH.API}/admin/influencer-profiles/${influencerId}/actual-campaigns-info?historical_period=${historicalPeriod}`,
      updatePayingStatus: (orgId: number) => `${ROOT_PATH.API}/admin/organizations/${orgId}/paying`,
      updateEndemicStatus: (orgId: number) => `${ROOT_PATH.API}/admin/organizations/${orgId}/endemic`,
      updateFreemiumAccount: (orgId: number) => `${ROOT_PATH.API}/admin/organizations/${orgId}/freemium-update`,
      getCampaignsWithUnreleasedPayouts: (brandProfileId: number) => `${ROOT_PATH.API}/admin/brand-profiles/${brandProfileId}/unreleased-payouts`,
      toggleAdminStatus: (userId: number) => `${ROOT_PATH.API}/admin/users/${userId}`,
      crawlProfile: (profileId: number) => `${ROOT_PATH.API}/admin/channel/${profileId}/crawl`,
      uploadSAPReport: () => `${ROOT_PATH.API}/reports/payments/sap/payment-info`,
      uploadExternalInvoicingInfluencers: () => `${ROOT_PATH.API}/reports/payments/sap/external-invoicing-influencers`,
      downloadSAPReportAsExcel: (start: string, end: string) => `${ROOT_PATH.API}/reports/payments/sap/xlsx?start_period=${start}&end_period=${end}`,
      downloadSAPReportAsCSV: (start: string, end: string) => `${ROOT_PATH.API}/reports/payments/sap/csv?start_period=${start}&end_period=${end}`,
      downloadInfluencersWithoutDebitorNumber: (startDate: string, endDate: string, includeDocs?: boolean) =>
        `${ROOT_PATH.API}/reports/payments/sap/influencers-without-debitor-number?start_period=${startDate}&end_period=${endDate}${includeDocs ? '&include_docs=true' : ''}`,
      downloadChangedPaymentReport: (startDate: string, endDate: string, includeDocs?: boolean) =>
        `${ROOT_PATH.API}/reports/payments/sap/changed-payment?start_period=${startDate}&end_period=${endDate}${includeDocs ? '&include_docs=true' : ''}`,
      getPayoutRound: (start: string, end: string) => `${ROOT_PATH.API}/reports/payments/payout-round?start_period=${start}&end_period=${end}`,
      reschedulePayout: () => `${ROOT_PATH.API}/payouts-plan/payouts/reschedule`,
      updateOriginForPayments: () => `${ROOT_PATH.API}/payouts/origin`,
      setHiddenFromSearchStatus: (profileId: number) => `${ROOT_PATH.API}/admin/influencer-profiles/${profileId}/is-hidden-from-search`,
      influencersAgencies: () => `${ROOT_PATH.API}/admin/influencers-agencies`,
      getPayoutRealmOverview: () => `${ROOT_PATH.API}/payout-realm-overview`,
      getInfluencersFromSameAgency: (influencerProfileId: number) => `${ROOT_PATH.API}/payout-realm-overview/creators/${influencerProfileId}/agency-profiles`,
      updateCcmStatus: (orgId: number) => `${ROOT_PATH.API}/admin/organizations/${orgId}/update-ccm-status`,
      USERS: {
        getDeletionRequestUsers: (includeDeleted?: boolean) => `${ROOT_PATH.API}/admin/users/deletion-requests?page=1&items_per_page=1000${includeDeleted ? '&include_deleted=true' : ''}`,
        deleteUser: (userId: number) => `${ROOT_PATH.API}/admin/users/${userId}`
      },
      CREATERLESS: {
        submitOutfit: (campaignId: number) => `${ROOT_PATH.API}/campaigns/creatorless/${campaignId}/outfits`,
        getOutfits: (campaignId: number) => `${ROOT_PATH.API}/campaigns/creatorless/${campaignId}/outfits`
      },
      REALM: {
        getProfiles: () => `${ROOT_PATH.API}/admin/outfit-creators`,
        saveProfile: (profileId: number) => `${ROOT_PATH.API}/outfit-creators/${profileId}`,
        importProfiles: () => `${ROOT_PATH.API}/admin/outfit-creators/grant-access`,
        updateProfileStatus: (profileId: number) => `${ROOT_PATH.API}/admin/outfit-creators/${profileId}/approval-status`,
        getProfilesTags: () => `${ROOT_PATH.API}/outfit-creators/tags`,
        getOutfits: () => `${ROOT_PATH.API}/admin/outfits`,
        downloadOutfitImages: (outfitId: number, filename: string) => `${ROOT_PATH.API}/outfits/${outfitId}/images/download?filename=${encodeURIComponent(filename)}`,
        exportOutfits: (statuses: string) => `${ROOT_PATH.API}/admin/outfits/excel/synced-outfit-reporting?statuses=${statuses}`,
        saveOutfitInfo: (outfitId: number) => `${ROOT_PATH.API}/admin/outfits/${outfitId}`,
        publishOutfit: (outfitId: number) => `${ROOT_PATH.API}/admin/outfits/${outfitId}/publish`,
        unpublishOutfit: (outfitId: number) => `${ROOT_PATH.API}/admin/outfits/${outfitId}/unpublish`,
        declineOutfit: (outfitId: number) => `${ROOT_PATH.API}/admin/outfits/${outfitId}/decline`,
        addOutfitItem: (outfitId: number) => `${ROOT_PATH.API}/outfits/${outfitId}/items`,
        deleteOutfitItem: (outfitId: number, itemId: number) => `${ROOT_PATH.API}/outfits/${outfitId}/items/${itemId}`,
        getEnableOutfitSectionLink: (influencerProfileId: number) => `${ROOT_PATH.API}/admin/influencer-profiles/${influencerProfileId}/realm`,
        checkOutfitSKU: (urlOrSKU: string) => `${ROOT_PATH.API}/outfits/items/info?url_or_sku=${urlOrSKU}`,
        outfitVideos: (outfitId: number) => `${ROOT_PATH.API}/outfits/${outfitId}/videos`,
        uploadOutfitVideo: () => `${ROOT_PATH.API}/upload/outfit-videos`,
        matchOutfitCreators: (influencerId: number, emsId?: string) => (
          isNil(emsId)
            ? `${ROOT_PATH.API}/outfit-creators/match?influencer_profile_id=${influencerId}`
            : `${ROOT_PATH.API}/outfit-creators/match?influencer_profile_id=${influencerId}&ems_id=${emsId}`
        )
      }
    },
    REALM_OUTFITS: {
      getOutfitsTags: () => `${ROOT_PATH.API}/outfits/tags?types=regular,gtl`,
      getOutfitsRegions: () => `${ROOT_PATH.API}/outfits/states`,
      saveOutfitInfo: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}`,
      publishOutfit: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/publish`,
      unpublishOutfit: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/unpublish`,
      declineOutfit: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/decline`,
      addOutfitItem: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/items`,
      deleteOutfitItem: (outfitId: number, itemId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/items/${itemId}`,
      outfitVideos: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/videos`,
      outfitAudioType: (outfitId: number, deliverableId: number, videoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/videos/${videoId}/audio-type`,
      getOutfit: (outfitId: number, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}`,
      deleteOutfits: (deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits`,
      downloadOutfitImages: (outfitId: number, filename: string, deliverableId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/images/download?filename=${encodeURIComponent(filename)}`,
      reTriggerVideoPipeline: (deliverableId: number, outfitId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/outfits/${outfitId}/videos/uploads`
    },
    ENTERTAINMENT_VIDEOS: {
      getEntertainmentVideo: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}`,
      updateEntertainmentVideo: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}`,
      addEntertainmentVideoItem: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}/items`,
      deleteEntertainmentVideoItem: (deliverableId: number, entertainmentVideoId: number, itemId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}/items/${itemId}`,
      declineEntertainmentVideo: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}/decline`,
      publishEntertainmentVideo: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}/publish`,
      unpublishEntertainmentVideo: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}/unpublish`,
      uploadEntertainmentVideoMedia: () => `${ROOT_PATH.API}/upload/entertainment-video-media`,
      uploadEntertainmentVideoThumbnail: () => `${ROOT_PATH.API}/upload/entertainment-video-thumbnails`,
      updateEntertainmentVideoMediaUrls: (deliverableId: number, entertainmentVideoId: number) => `${ROOT_PATH.API}/deliverables/${deliverableId}/entertainment-videos/${entertainmentVideoId}/videos`
    },
    MESSENGER: {
      getConversations: () => `${ROOT_PATH.API}/conversations`,
      getPaginatedConversations: (queryString: string) => `${ROOT_PATH.API}/paginated-conversations?${queryString}`,
      getConversationsCount: (campaignId: number) => `${ROOT_PATH.API}/conversations-meta?campaign_id=${campaignId}`,
      getConversationMessages: (conversationExternalId: string) => `${ROOT_PATH.API}/conversations/${conversationExternalId}/messages`,
      getConversationAttachments: (conversationExternalId: string) => `${ROOT_PATH.API}/conversations/${conversationExternalId}/attachments`,
      getConversationlinks: (conversationExternalId: string) => `${ROOT_PATH.API}/conversations/${conversationExternalId}/links`,
      markConversationAsUnread: (conversationExternalId: string) => `${ROOT_PATH.API}/conversations/${conversationExternalId}/unread`,
      sendMessage: (conversationId: number | string) => `${ROOT_PATH.API}/conversations/${conversationId}/messages`,
      sendAttachmentMessage: (conversationId: number | string) => `${ROOT_PATH.API}/conversations/${conversationId}/attachments`,
      sendBulkMessage: () => `${ROOT_PATH.API}/conversations/messages`,
      sendBulkAttachment: () => `${ROOT_PATH.API}/conversations/attachments`,
      getUnreadMessages: () => `${ROOT_PATH.API}/conversations/unread`,
      deleteMessage: (conversationId: number | string, messageId: number | string) => `${ROOT_PATH.API}/conversations/${conversationId}/messages/${messageId}`
    },
    CONTENT_DISCOVERY: {
      getResults: (brandProfileId: number, page: number, sort: string, itemsPerPage: number) =>
        `${ROOT_PATH.API}/brands/${brandProfileId}/content-discovery?page=${page}&sort=${sort}&items_per_page=${itemsPerPage}`
    },
    SOV: getInsightsEndpoints('sov'),
    INSIGHTS_REPORTS: getInsightsEndpoints('insights'),
    CI: {
      SOV: {
        getReport: () => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports`
        ),
        getQueries: (reportId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries`
        ),
        getQueriesMulti: (reportId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/multi`
        ),
        getQuery: (reportId: NumberNullUndefined, queryId: number) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${queryId}`
        ),
        getChannels: () => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/channels`
        ),
        getCountries: () => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/countries`
        ),
        getKeywords: (name: string, queryId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/queries/${name}/search?query_id=${valueOrEmptyString(queryId)}`
        ),
        createQuery: (reportId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries`
        ),
        createQueryMulti: (reportId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/multi`
        ),
        updateQuery: (reportId: NumberNullUndefined, queryId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${queryId}`
        ),
        updateQueryMulti: (reportId: NumberNullUndefined, queryId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${String(queryId)}/multi`
        ),
        deleteQuery: (reportId: NumberNullUndefined, queryId: number) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${queryId}`
        ),
        deleteQueryMulti: (reportId: NumberNullUndefined, queryId: number) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${queryId}/multi`
        ),
        cloneQuery: (reportId: NumberNullUndefined, queryId: number) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${queryId}/clone`
        ),
        cloneQueryMulti: (reportId: NumberNullUndefined, queryId: number) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/reports/${reportId}/queries/${queryId}/clone/multi`
        ),
        updateReport: (reportId: number, brandProfileId: NumberNullUndefined) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/${brandProfileId}/reports/${reportId}?saved=true`
        ),
        runAnalysis: (reportId: NumberNullUndefined, reportType: SOVReportType) => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/${reportType}/reports/${reportId}/analysis`
        ),
        checkAccess: () => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/access/check`
        ),
        updateAccess: () => (
          `${ROOT_PATH.API}/consumer-insights/v2/sov/access/update`
        )
      }
    },
    INFLUENCER_GRAPHS: {
      getInfluencerGraphs: (influencerProfileId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerProfileId}/graphs`
    },
    CAMPAIGN_DASHBOARD: {
      getStatistics: (chartType: ChartType, campaignId: number, query?: string | null) => (
        `${ROOT_PATH.API}/campaigns/${campaignId}/dashboard-${chartType}-stats${query ? `?${query}` : ''}`
      )
    },
    USERS: {
      updateActiveOrganization: (userId: number) => `${ROOT_PATH.API}/users/${userId}/active-organization`,
      trackActivity: (userId: number) => `${ROOT_PATH.API}/users/${userId}/actions`,
      deleteUser: (userId: number) => `${ROOT_PATH.API}/users/${userId}`,
      updateUser: (userId: number) => `${ROOT_PATH.API}/users/${userId}`
    },
    API: {
      CONFIG: ROOT_PATH.CONFIG,
      ADMIN_GET_BRANDS: `${ROOT_PATH.API}/admin/brand-profiles`,
      ADMIN_ORGANIZATIONS: `${ROOT_PATH.API}/admin/organizations`,
      ADMIN_AGENCIES: `${ROOT_PATH.API}/admin/agencies`,
      NEW_SESSIONS: `${ROOT_PATH.IDENTITY}/sessions`,
      GET_CURRENT_USER: `${ROOT_PATH.API}/current-user`,
      NEW_USERS: `${ROOT_PATH.API}/users`,
      ORGANIZATIONS: `${ROOT_PATH.API}/organizations`,
      ORGANIZATION_REPORTS: `${ROOT_PATH.API}/organizations`,
      TAXONOMIES: `${ROOT_PATH.API}/taxonomies`,
      REQUEST_PASSWORD_RESET: `${ROOT_PATH.API}/actions`,
      ACTIONS: `${ROOT_PATH.API}/actions`,
      IMAGES: `${ROOT_PATH.API}/image`,
      UPLOAD: `${ROOT_PATH.API}/upload`,
      UPLOAD_DOCUMENT: `${ROOT_PATH.API}/upload/proof-documents`,
      STREAM_CLOUDINARY: `${ROOT_PATH.API}/cloudinary-video`,
      COUNTRIES: `${ROOT_PATH.API}/countries`,
      EMAIL_UNSUBSCRIPTION: `${ROOT_PATH.API}/email-unsubscription`,
      DECLINE_LIGHTWEIGHT_CAMPAIGN: `${ROOT_PATH.API}/decline-lightweight-invitation`,
      acceptInvitation: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/accept-invitation`,
      declineInvitation: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/decline-invitation`,
      getActiveInvitation: (userId: number) => `${ROOT_PATH.API}/users/${userId}/active-invitations`,
      updateZalandoMeta: (organizationId: string) => `${ROOT_PATH.API}/admin/organizations/${organizationId}/zalando-meta`,
      getMerchants: (query: string) => `${ROOT_PATH.API}/campaign-metadata/merchants/search?query=${query}&type=starts_with`,
      getBrands: (query: string) => `${ROOT_PATH.API}/campaign-metadata/brands/search?query=${query}&include_brand_family=true`,
      getCampaignPayments: (campaignId: number) => `${ROOT_PATH.API}/campaigns/${campaignId}/payments`,
      getSubcampaignPayments: (subcampaignId: number) => `${ROOT_PATH.API}/subcampaigns/${subcampaignId}/payments`,
      getAudienceData: (channelUserName: string, channelType: string) => `${ROOT_PATH.API}/audience/${channelType}/${channelUserName}`,
      getSubscription: (organizationId: number) => `${ROOT_PATH.API}/organizations/${organizationId}/subscription`,
      getSubscriptionByProduct: (organizationId: number, productName: string) => `${ROOT_PATH.API}/organizations/${organizationId}/subscription?product=${productName}`,
      getNotes: (orgId: number, influencerId: number, filter: string, campaignId?: number | null) => {
        const filterQuery = filter ? `&filter_by=${filter}` : '';
        return `${ROOT_PATH.API}/organizations/${orgId}/notes/?influencer_profile_id=${influencerId}${filterQuery}${campaignId ? `&campaign_id=${campaignId}` : ''}`;
      },
      getBrandsThatHaveAddedNotes: (influencerId: number) => `${ROOT_PATH.API}/influencer-profiles/${influencerId}/notes`,
      getNoteCreate: (orgId: number) => `${ROOT_PATH.API}/organizations/${orgId}/notes`,
      getNoteUpdate: (orgId: number, noteId: number) => `${ROOT_PATH.API}/organizations/${orgId}/notes/${noteId}`,
      getNoteDelete: (orgId: number, noteId: number) => `${ROOT_PATH.API}/organizations/${orgId}/notes/${noteId}`,
      getOverdueDeliverables: (subcampaignsIds: Array<number>) => `${ROOT_PATH.API}/overdue-deliverables?subcampaign_ids=${subcampaignsIds.join(',')}`,
      getShippingAddresses: (orgId: number, shippingId?: number) => `${ROOT_PATH.API}/organizations/${orgId}/shipping_addresses/${!isNil(shippingId) ? shippingId : ''}`
    },
    SHARED_ACCESS: {
      uploadFile: () => `${ROOT_PATH.API}/shared-access/file`,
      fetchMeta: (token: string) => `${ROOT_PATH.API}/shared-access/${token}`,
      fetchCampaign: () => `${ROOT_PATH.API}/shared-access/content-approval/campaign`,
      fetchBuckets: (influencerId: string, page: number, sort: string) => (
        `${ROOT_PATH.API}/shared-access/content-approval/buckets?${influencerId ? `creator_id=${influencerId}&` : ''}page=${page}&sort_by=${sort}`
      ),
      fetchBucketsV2: (influencerId: string, page: number, sort: string) => (
        `${ROOT_PATH.API}/shared-access/content-approval/v2/buckets?${influencerId ? `creator_id=${influencerId}&` : ''}page=${page}&sort_by=${sort}&include_task_name=true&requires_caption_approval=true`
      ),
      markAsFavourite: (bucketId: number, itemId: number) => `${ROOT_PATH.API}/shared-access/content-approval/v2/buckets/${bucketId}/items/${itemId}/favourite`,
      fetchCreators: () => `${ROOT_PATH.API}/shared-access/content-approval/creators`,
      updateBucketNotes: (bucketId: number) => `${ROOT_PATH.API}/shared-access/content-approval/buckets/${bucketId}`,
      updateBucketNotesV2: (bucketId: number) => `${ROOT_PATH.API}/shared-access/content-approval/v2/buckets/${bucketId}/approval-status`,
      markNotesAsReadV2: (bucketId: number) => `${ROOT_PATH.API}/shared-access/content-approval/v2/buckets/${bucketId}/approval-notes/read`,
      updateBucketItemApprovalStatus: (bucketId: number, bucketItemId: number) =>
        `${ROOT_PATH.API}/shared-access/content-approval/buckets/${bucketId}/items/${bucketItemId}`,
      downloadDeliverables: (token: string, filename: string) => `${ROOT_PATH.API}/shared-access/content-approval/download?token=${token}&filename=${encodeURIComponent(filename)}`,
      downloadLook: (bucketId: number, token: string, filename: string) => `${ROOT_PATH.API}/shared-access/content-approval/download/buckets/${bucketId}?token=${token}&filename=${encodeURIComponent(filename)}`
    },
    INSTAGRAM_POSTS: {
      deletePosts: () => `${ROOT_PATH.API}/instagram/posts`,
      fetchPostImage: (postId: string) => `${ROOT_PATH.API}/posts/instagram/${postId}/image`,
      fetchPostImageContent: (postId: string) => `${ROOT_PATH.COLLAB_BE}/posts/instagram/image/${postId}/content`,
      fetchSharedPostImage: (postId: string) => `${ROOT_PATH.API}/shared-access/posts/instagram/${postId}/image`
    },
    TIKTOK_POSTS: {
      deletePosts: () => `${ROOT_PATH.API}/tiktok/posts`,
      fetchPostImage: (postUrl: string) => `${ROOT_PATH.API}/posts/tiktok/image?url=${postUrl}`,
      fetchSharedPostImage: (postUrl: string) => `${ROOT_PATH.API}/shared-access/posts/tiktok/image?url=${postUrl}`
    },
    PDFKITTY: {
      generateIndividualOfferPDF: () => `${ROOT_PATH.PDFKITTY}/individual-offers`
    },
    CREATOR_PROPOSAL: {
      creatorListInfo: (organizationId: number, creatorListId: number) =>
        `${ROOT_PATH.API}/organizations/${organizationId}/lists/${creatorListId}/creator-approval-list`,
      creatorList: (organizationId: number, creatorListId: number, queryString: string) =>
        `${ROOT_PATH.API}/organizations/${organizationId}/lists/${creatorListId}/creator-approval-list-members?${queryString}`,
      loadAllCreatorsContent: (organizationId: number, creatorListId: number) =>
        `${ROOT_PATH.API}/organizations/${organizationId}/lists/${creatorListId}/creator-approval-list-members/load-all-content`,
      creatorListMember: (organizationId: number, creatorListId: number, memberId: number) =>
        `${ROOT_PATH.API}/organizations/${organizationId}/lists/${creatorListId}/creator-approval-list-members/${memberId}`,
      creatorRecentMedia: (profileId: number, channelId: number, page: number) =>
        `${ROOT_PATH.API}/influencer-profiles/${profileId}/channels/${channelId}/posts?page=${page}&items_per_page=30`,
      creatorListInfluencerStatuses: (organizationId: number, creatorListId: number, influencerIds: Array<number>) =>
        `${ROOT_PATH.API}/organizations/${organizationId}/lists/${creatorListId}/creator-approval-list-members/status?influencerIds=${influencerIds.join(',')}`,
      generateCreatorReport: (organizationId: number, creatorListId: number) =>
        `${ROOT_PATH.API}/organizations/${organizationId}/lists/${creatorListId}/report/async/creators`,
      checkCreatorReport: (taskId: number) =>
        `${ROOT_PATH.API}/report/async/task/${taskId}/status`,
      downloadCreatorReport: (taskId: number) =>
        `${ROOT_PATH.API}/report/async/task/${taskId}/content`,
      extendExternalPageToken: (orgId: number, listId: number) =>
        `${ROOT_PATH.API}/organizations/${orgId}/lists/${listId}/creator-approval-list/extend-token`
    },
    CREATOR_APPROVAL: {
      creatorListInfo: () =>
        `${ROOT_PATH.API}/shared-access/creator-approval-list`,
      creatorList: (queryString: string) =>
        `${ROOT_PATH.API}/shared-access/creator-approval-list-members?${queryString}`,
      creatorListMember: (approvalListId: number, memberId: number) =>
        `${ROOT_PATH.API}/shared-access/creator-approval-list/${approvalListId}/members/${memberId}`,
      setListMemberApproval: (approvalListId: number, memberId: number) =>
        `${ROOT_PATH.API}/shared-access/creator-approval-list/${approvalListId}/members/${memberId}/approval-status`
    },
    SHARED_OUTFITS: {
      sharedOutfit: () => `${ROOT_PATH.API}/shared-access/campaign-outfit`,
      articleInfo: (query: string) => `${ROOT_PATH.API}/shared-access/campaign-outfit/item-info?${query}`,
      downloadOutfit: () => `${ROOT_PATH.API}/shared-access/campaign-outfit/excel`,
      deleteOutfit: (outfitId: number) => `${ROOT_PATH.API}/shared-access/campaign-outfit/${outfitId}`
    },
    SHARED_PRODUCT_LIST: {
      sharedProductListInfo: () =>
        `${ROOT_PATH.API}/shared-access/product-list`,
      sharedProductListItems: (queryString: string) =>
        `${ROOT_PATH.API}/shared-access/product-list/items?${queryString}`,
      sharedProductListFilters: () => `${ROOT_PATH.API}/shared-access/product-list/dropdowns`
    },
    TUTORIALS: {
      tutorialHistory: () => `${ROOT_PATH.API}/tutorials/history`
    },
    MISC: {
      addBrands: () => `${ROOT_PATH.API}/instagram/channels/crawl`
    },
    LOCATIONS: {
      getTeleportProxy: (type: string) => `${ROOT_PATH.API}/proxy/teleport/${type}`,
      getLocations: () => `${ROOT_PATH.API}/cities`
    },
    INFLUENCERS_AGENCIES: {
      influencersAgency: (agencyId: number) => `${ROOT_PATH.API}/influencers-agencies/${agencyId}`,
      influencersAgencyProfiles: (agencyId: number, query: string) => `${ROOT_PATH.API}/influencers-agencies/${agencyId}/influencer-profiles?${query}`,
      createInfluencerProfilesForAgency: (agencyId: number) => `${ROOT_PATH.API}/influencers-agencies/${agencyId}/influencer-profiles/bulk`,
      deleteInfluecnerProfile: (agencyId: number, profileId: number) => `${ROOT_PATH.API}/influencers-agencies/${agencyId}/influencer-profiles/${profileId}`
    },
    SSE: {
      getSSEEndpointURL: (authToken: string) => `//${ROOT_PATH.RAVEN}/sse/${authToken}`
    }
  }
};

function extendRequestHeaders(headers, suppressAuth, suppressCollabHeaders = false) {
  // @see: https://tools.ietf.org/html/rfc6750
  const authenticationHeader = suppressAuth ? {} : { Authorization: `Bearer ${getAccessToken()}` };

  // @see https://opensource.zalando.com/restful-api-guidelines/#183
  const customFrontendTypeHeader = suppressCollabHeaders ? {} : { 'X-Frontend-Type': 'browser' };

  // $FlowIgnore - Computing object literal [1] may lead to an exponentially large number of cases to reason about
  return { ...headers, ...authenticationHeader, ...customFrontendTypeHeader };
}

/**
 * A simplistic wrapper around "axios" which is a facade for XHR
 * but with an enriched interface of "fetch()".
 *
 * Some juicy features:
 * - comprehensive config
 * - interceptors, transformations
 * - error handling abstraction
 * - concurrent requests
 * - progress events
 * - ...
 *
 * @see: https://github.com/mzabriskie/axios#axios-api
 */
export function request(
  method: ApiMethod,
  url: string,
  config: Partial<ApiConfig> = {},
  options: Partial<ApiOptions> = {}
): Promise<any> {
  // @todo: check if "params" are really needed
  // "maxContentLength" allows Reporting (potential huge response) to override the default 2000 limit
  const {
    params,
    data,
    headers,
    maxContentLength,
    responseType,
    cancelToken,
    onUploadProgress
  } = config;

  // non-axios specific params
  const {
    suppressAuth,
    suppressCollabHeaders,
    isRepeatingRequest,
    suppressGlobalNotification
  } = options;

  return axios({
    method,
    url,
    params,
    data: data instanceof File ? data : snakeCaseKeys(trimPayload(data)),
    headers: extendRequestHeaders(headers, suppressAuth, suppressCollabHeaders),
    maxContentLength: maxContentLength || -1,
    responseType,
    cancelToken,
    onUploadProgress
  })
    .then((response) => ({
      ...response,
      data: camelCaseKeys(response.data)
    }))
    .catch((error) => {
      logError(error);

      if (!error.response) {
        throw error; // e.g, js error
      }

      const status = error.response.status;
      const code = error.response.data ? error.response.data.code : null;

      if (status === UNAUTHORIZED_STATUS_CODE &&
        (code === API_CODE_TOKEN_INVALID || isRepeatingRequest) &&
        !suppressAuth
      ) {
        return (new Promise(
          (resolve, reject) => {
            if (isRepeatingRequest) {
              // second attempt is not allowed
              reject(error);
              return;
            }

            const accessToken = getAccessToken();
            const refreshToken = getRefreshToken();

            if (accessToken && refreshToken) {
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              renewToken(accessToken, refreshToken)
                // session extended, repeat the same request again
                .then(() => resolve(request(method, url, config, {
                  ...options,
                  isRepeatingRequest: true
                })))
                .catch(() => reject(error));
            } else {
              reject(error);
            }
          }))
          .catch(() => {
            // if not a js error and status is 401
            // report consequent authentication failure
            // and force logout
            if (
              error.response.status
              && error.response.status === UNAUTHORIZED_STATUS_CODE
            ) {
              dispatchWithShameNotificationAction({
                status: UNAUTHORIZED_STATUS_CODE,
                type: 'info',
                message: __t('api_util.session_expired_error'),
                keepBetweenPages: true
              });
            }
            // don't suppress, it should be handled in each reducer
            throw error;
          });
      }

      if (status === UNAUTHORIZED_STATUS_CODE && (code === API_CODE_TOKEN_REVOKED || code === API_CODE_CREDENTIALS_MISSING) && !suppressAuth) {
        // terminate the session
        dispatchWithShameNotificationAction({
          status: UNAUTHORIZED_STATUS_CODE,
          type: 'info',
          message: __t('api_util.session_expired_error'),
          keepBetweenPages: true
        });

        // don't suppress, it should be handled in each reducer
        throw error;
      }

      if (status === NOT_FOUND_STATUS_CODE) {
        if (!suppressGlobalNotification) {
          dispatchWithShameNotificationAction({
            status: NOT_FOUND_STATUS_CODE
          });
        }

        throw error;
      }

      throw error; // throw initial error
    });
}

// TODO remove any once we are completely migrated to TS
export function get(url: string, config?: Partial<ApiConfig>, options?: Partial<ApiOptions>): Promise<any> {
  return request('GET', url, config, options);
}

export function post(url: string, config?: Partial<ApiConfig>, options?: Partial<ApiOptions>): Promise<any> {
  return request('POST', url, config, options);
}

export function put(url: string, config?: Partial<ApiConfig>, options?: Partial<ApiOptions>): Promise<any> {
  return request('PUT', url, config, options);
}

export function patch(url: string, config?: Partial<ApiConfig>, options?: Partial<ApiOptions>): Promise<any> {
  return request('PATCH', url, config, options);
}

// not "delete()" because of reserved word
export function destroy(url: string, config?: Partial<ApiConfig>, options?: Partial<ApiOptions>): Promise<any> {
  return request('DELETE', url, config, options);
}

// could not put in sessions reducer because of an issue with circular dependencies
function renewToken(accessToken, refreshToken) {
  const decodedAccessToken = decodeToken(accessToken);

  if (!decodedAccessToken || !decodedAccessToken.session_id) {
    return Promise.reject(null); // eslint-disable-line prefer-promise-reject-errors
  }

  const url = API.END_POINTS.API.NEW_SESSIONS;
  const data = {
    refreshToken
  };

  return put(url, { data }, { isRepeatingRequest: true })
    .then((response: any) => {
      const tokens = response.data?.token ?? response.data;
      const roles = decodeToken(tokens.accessToken)?.roles ?? [];

      receiveRoles(roles);
      setTokens(tokens);
    });
}
